import { lastPeriodOfYear } from "utils/getIsoPeriodStart";

// Column 0 of the grid is the KPI label column, so window period n is column n + 1.
const labelColumnOffset = 1;

// The per-column fill states. Window membership is tracked separately, because the anchor is inside
// the window while still being drawn as an open period.
const openState = "open";
const alertState = "alert";
const plainState = "plain";
const countingState = "counting";

// The fill class per state. A plain column carries no tint.
const stateFillClasses = {
    [openState]: "er-kpi-col-open",
    [alertState]: "er-kpi-col-alert",
    [countingState]: "er-kpi-col-counting"
};

const leftDividerClass = "er-kpi-col-divider";
const rightDividerClass = "er-kpi-col-divider-right";

// Period ordinal used to compare a period against the counting-window bounds.
function periodOrdinal(isoYear, isoPeriod) {
    return isoYear * lastPeriodOfYear + isoPeriod;
}

function isAtOrAfter(period, bound) {
    return periodOrdinal(period.isoYear, period.isoPeriod) >= periodOrdinal(bound.isoYear, bound.isoPeriod);
}

function isAtOrBefore(period, bound) {
    return periodOrdinal(period.isoYear, period.isoPeriod) <= periodOrdinal(bound.isoYear, bound.isoPeriod);
}

// The ordinals an alert was raised in, taken from the response's alert rows. The period's own
// hasTrigger flag is NOT the alert flag — it is set for any period holding a breached KPI cell, which
// is most of them — so the alert shading has to come from the alerts themselves.
function buildAlertOrdinals(alerts) {
    const ordinals = new Set();

    if (alerts == null) {
        return ordinals;
    }

    alerts.forEach((alert) => {
        ordinals.add(periodOrdinal(alert.isoYear, alert.isoPeriod));
    });

    return ordinals;
}

// Resolves a period's shading state. An open period wins over an alert: alerts land at the open
// anchors, and a trigger in a period still accumulating can be rectified before the period closes.
function resolveState(period, countingWindow, alertOrdinals) {
    if (period.isOpen === true) {
        return openState;
    }

    if (alertOrdinals.has(periodOrdinal(period.isoYear, period.isoPeriod))) {
        return alertState;
    }

    return isInWindow(period, countingWindow) ? countingState : plainState;
}

// Whether a period falls inside the counting window. This is deliberately separate from the fill
// state: the anchor sits inside the window but is still open, so it is drawn blue while the dividers
// enclose it. The dividers say what counts; the fill says whether the figures can still change.
function isInWindow(period, countingWindow) {
    if (countingWindow == null || period.isBeforeFloor === true) {
        return false;
    }

    return isAtOrAfter(period, countingWindow.windowStart) && isAtOrBefore(period, countingWindow.windowEnd);
}

// Whether a period falls in the most recent three of the window — the span the short-window trigger
// conditions look at, ending at the anchor exactly as the window itself does.
function isInThree(period, countingWindow) {
    return isInWindow(period, countingWindow)
        && countingWindow.threePeriodBoundary != null
        && isAtOrAfter(period, countingWindow.threePeriodBoundary);
}

// Builds the ListingTable columnClasses array for the KPI grid: one entry per column, shading each
// period column from its own state and bracketing the counting block with dividers. Every divider is
// derived by comparing a column with its immediate neighbours, so the classes stay correct whatever
// order the period columns are rendered in. Suppressing the dividers leaves the fills untouched, so
// the counting window still colours the grid without the lines being drawn.
export function buildKpiColumnClasses(periods, countingWindow, alerts, showAssessmentLines = true) {
    const alertOrdinals = buildAlertOrdinals(alerts);
    const states = periods.map((period) => resolveState(period, countingWindow, alertOrdinals));
    const inWindow = periods.map((period) => isInWindow(period, countingWindow));
    const inThree = periods.map((period) => isInThree(period, countingWindow));

    const columnClasses = [""];

    states.forEach((state, index) => {
        const classes = [];
        const fillClass = stateFillClasses[state];
        if (fillClass != null) {
            classes.push(fillClass);
        }

        if (showAssessmentLines) {
            const previousInWindow = index > 0 ? inWindow[index - 1] : false;
            const nextInWindow = index < periods.length - 1 ? inWindow[index + 1] : false;
            const previousInThree = index > 0 ? inThree[index - 1] : false;
            const nextInThree = index < periods.length - 1 ? inThree[index + 1] : false;

            if (inWindow[index]) {
                if (!previousInWindow) {
                    classes.push(leftDividerClass);
                }
                if (!nextInWindow) {
                    classes.push(rightDividerClass);
                }
            }

            // The inner divider separates the most recent three from the rest of the window. It is
            // drawn against whichever side the rest of the window sits on, so it stays correct when
            // the column order is reversed. A window of three periods or fewer has no counting column
            // outside the three, so no inner line is drawn.
            if (inThree[index]) {
                if (previousInWindow && !previousInThree) {
                    classes.push(leftDividerClass);
                }
                if (nextInWindow && !nextInThree) {
                    classes.push(rightDividerClass);
                }
            }
        }

        columnClasses[index + labelColumnOffset] = classes.join(" ");
    });

    return columnClasses;
}

// True when at least one period in the window carries the marker, so the grid knows to reserve the
// slot on every column header.
export function hasCappedEndedPeriod(periods) {
    return periods.some(isCappedEndedPeriod);
}

// True when the marker should be drawn on a period's column header. The current period is the only
// exclusion — its day and fleet counts are still accumulating, so the flag is not yet meaningful
// there. Every earlier period has finished accumulating, including the previous one, which is open
// only while its figures are reconciled.
export function isCappedEndedPeriod(period) {
    return period.isSmallOperatorCapped === true && period.isCurrent !== true;
}
